import { useMemo, useState } from 'react';
import { Badge, Button, DataTable, Input } from 'lift-ui';
import type { TableAction, TableColumn } from 'lift-ui';
import { RefreshCw, Trash2, Play, Eye } from 'lucide-react';
import {
  useDiscardAll,
  useDiscardJob,
  useDispatchJob,
  useJobs,
  useJobsAccess,
  useRetryAll,
  useRetryJob,
} from '../hooks';
import type { JobStatus, JobSummary } from '../types';
import { formatCount, formatTimestamp } from '../utils';
import { JobStatusBadge, STATUS_LABEL } from './JobStatusBadge';
import { ConfirmDialog } from './ConfirmDialog';

const PAGE_SIZE = 25;

interface JobsTabProps {
  supportedStatuses: JobStatus[];
  onSelectJob: (jobId: string) => void;
}

type PendingBulk = 'retry' | 'discard' | null;

export const JobsTab = ({ supportedStatuses, onSelectJob }: JobsTabProps) => {
  const { canEdit, canManage } = useJobsAccess();

  const [status, setStatus] = useState<JobStatus>(
    supportedStatuses.includes('failed') ? 'failed' : (supportedStatuses[0] ?? 'pending')
  );
  const [jobClassName, setJobClassName] = useState('');
  const [queueName, setQueueName] = useState('');
  const [page, setPage] = useState(1);
  const [pendingBulk, setPendingBulk] = useState<PendingBulk>(null);
  const [jobToDiscard, setJobToDiscard] = useState<JobSummary | null>(null);

  const filters = useMemo(
    () => ({
      status,
      job_class_name: jobClassName || undefined,
      queue_name: queueName || undefined,
      page,
      page_size: PAGE_SIZE,
    }),
    [status, jobClassName, queueName, page]
  );

  const { data, isLoading, isFetching, refetch } = useJobs(filters);
  const retryJob = useRetryJob();
  const dispatchJob = useDispatchJob();
  const discardJob = useDiscardJob();
  const retryAll = useRetryAll();
  const discardAll = useDiscardAll();

  const bulkFilters = { job_class_name: jobClassName || undefined, queue_name: queueName || undefined };
  const isMutating =
    retryJob.isPending || dispatchJob.isPending || discardJob.isPending ||
    retryAll.isPending || discardAll.isPending;

  const changeStatus = (next: JobStatus) => {
    setStatus(next);
    setPage(1);
  };

  const columns: TableColumn<JobSummary>[] = [
    {
      key: 'job_class_name',
      header: 'Job',
      accessor: 'job_class_name',
      render: (_value, row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.job_class_name}</span>
          <span className="text-xs text-muted-foreground font-mono">{row.job_id}</span>
        </div>
      ),
    },
    {
      key: 'queue_name',
      header: 'Queue',
      accessor: 'queue_name',
      render: (_value, row) => <Badge variant="greyLight">{row.queue_name}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      accessor: 'status',
      render: (_value, row) => <JobStatusBadge status={row.status} />,
    },
    {
      key: 'arguments',
      header: 'Arguments',
      accessor: 'arguments',
      render: (_value, row) => (
        <span className="text-xs font-mono text-muted-foreground line-clamp-2">
          {row.arguments.length ? row.arguments.join(', ') : '—'}
        </span>
      ),
    },
    {
      key: 'error',
      header: 'Error',
      accessor: 'error',
      render: (_value, row) =>
        row.error ? (
          <span className="text-xs text-destructive line-clamp-2">
            {row.error.error_class}: {row.error.message}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'enqueued_at',
      header: 'Enqueued',
      accessor: 'enqueued_at',
      render: (_value, row) => (
        <span className="text-xs whitespace-nowrap">{formatTimestamp(row.enqueued_at)}</span>
      ),
    },
  ];

  const actions: TableAction<JobSummary>[] = [
    {
      label: 'View',
      icon: <Eye size={14} />,
      variant: 'ghost',
      onClick: (row) => onSelectJob(row.job_id),
    },
  ];

  if (canEdit) {
    actions.push(
      {
        label: 'Retry',
        icon: <RefreshCw size={14} />,
        variant: 'outline',
        disabled: (row) => row.status !== 'failed' || isMutating,
        onClick: (row) => retryJob.mutate(row.job_id),
      },
      {
        label: 'Dispatch',
        icon: <Play size={14} />,
        variant: 'outline',
        disabled: (row) => !['scheduled', 'blocked'].includes(row.status ?? '') || isMutating,
        onClick: (row) => dispatchJob.mutate(row.job_id),
      }
    );
  }

  if (canManage) {
    actions.push({
      label: 'Discard',
      icon: <Trash2 size={14} />,
      variant: 'destructive',
      disabled: () => isMutating,
      onClick: (row) => setJobToDiscard(row),
    });
  }

  const totalItems = data?.pagination.total_count ?? data?.data.length ?? 0;
  const totalPages = data?.pagination.total_pages ?? 1;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap gap-1">
          {supportedStatuses.map((option) => (
            <Button
              key={option}
              size="sm"
              variant={option === status ? 'default' : 'outline'}
              onClick={() => changeStatus(option)}
            >
              {STATUS_LABEL[option]}
            </Button>
          ))}
        </div>

        <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
          <Input
            className="w-56"
            placeholder="Filter by job class"
            value={jobClassName}
            onChange={(event) => {
              setJobClassName(event.target.value);
              setPage(1);
            }}
          />
          <Input
            className="w-44"
            placeholder="Filter by queue"
            value={queueName}
            onChange={(event) => {
              setQueueName(event.target.value);
              setPage(1);
            }}
          />
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </Button>
        </div>
      </div>

      {status === 'failed' && (canEdit || canManage) && (
        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
          <span className="text-sm text-muted-foreground">
            Bulk actions apply to failed jobs matching the current filters.
          </span>
          <div className="ml-auto flex gap-2">
            {canEdit && (
              <Button
                size="sm"
                variant="outline"
                disabled={isMutating || totalItems === 0}
                onClick={() => setPendingBulk('retry')}
              >
                Retry all
              </Button>
            )}
            {canManage && (
              <Button
                size="sm"
                variant="destructive"
                disabled={isMutating || totalItems === 0}
                onClick={() => setPendingBulk('discard')}
              >
                Discard all
              </Button>
            )}
          </div>
        </div>
      )}

      <DataTable<JobSummary>
        data={data?.data ?? []}
        columns={columns}
        actions={actions}
        loading={isLoading}
        emptyMessage={`No ${STATUS_LABEL[status].toLowerCase()} jobs`}
        onRowClick={(row) => onSelectJob(row.job_id)}
        rowClickable
        stickyHeader
        pagination={{
          currentPage: page,
          totalPages: totalPages ?? 1,
          pageSize: PAGE_SIZE,
          totalItems,
          onPageChange: setPage,
          onPageSizeChange: () => undefined,
          pageSizeOptions: [PAGE_SIZE],
        }}
      />

      <p className="text-xs text-muted-foreground">
        Showing page {page} of {totalPages ?? 1} · {formatCount(data?.pagination.total_count)} jobs
      </p>

      <ConfirmDialog
        open={pendingBulk === 'retry'}
        title="Retry all failed jobs?"
        description="Every failed job matching the current filters will be queued to run again."
        confirmLabel="Retry all"
        pending={retryAll.isPending}
        onCancel={() => setPendingBulk(null)}
        onConfirm={() => {
          retryAll.mutate(bulkFilters, { onSettled: () => setPendingBulk(null) });
        }}
      />

      <ConfirmDialog
        open={pendingBulk === 'discard'}
        title="Discard all failed jobs?"
        description="Every failed job matching the current filters will be deleted. This cannot be undone."
        confirmLabel="Discard all"
        destructive
        pending={discardAll.isPending}
        onCancel={() => setPendingBulk(null)}
        onConfirm={() => {
          discardAll.mutate(bulkFilters, { onSettled: () => setPendingBulk(null) });
        }}
      />

      <ConfirmDialog
        open={Boolean(jobToDiscard)}
        title="Discard this job?"
        description={`${jobToDiscard?.job_class_name ?? 'The job'} will be deleted. This cannot be undone.`}
        confirmLabel="Discard"
        destructive
        pending={discardJob.isPending}
        onCancel={() => setJobToDiscard(null)}
        onConfirm={() => {
          if (jobToDiscard) {
            discardJob.mutate(jobToDiscard.job_id, { onSettled: () => setJobToDiscard(null) });
          }
        }}
      />
    </div>
  );
};

export default JobsTab;
